import { accountAxios } from '@/services/api/accountAxios';
import type {
  ApiEnvelope,
  OrganizationInvitation,
  OrganizationMember,
  OrganizationProfile,
  OrganizationProfileUpdate,
  OrganizationRole,
  PaginatedEnvelope,
} from '@/services/api/accountTypes';

/**
 * Organization surface. These routes resolve the org from the bearer token —
 * no organizationId in the path — so they cannot read another tenant.
 */
export const accountOrganizationApi = {
  getProfile: async (): Promise<OrganizationProfile> => {
    const response = await accountAxios.get<ApiEnvelope<OrganizationProfile>>(
      'organizations/profile'
    );
    return response.data.data;
  },

  updateProfile: async (input: OrganizationProfileUpdate): Promise<OrganizationProfile> => {
    const response = await accountAxios.patch<ApiEnvelope<OrganizationProfile>>(
      'organizations/profile',
      input
    );
    return response.data.data;
  },

  /** Roles available to assign on an invitation. */
  getRoles: async (): Promise<OrganizationRole[]> => {
    const response = await accountAxios.get<PaginatedEnvelope<OrganizationRole[]>>('roles');
    return response.data.data ?? [];
  },

  getMembers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<{ members: OrganizationMember[]; total: number }> => {
    const response = await accountAxios.get<PaginatedEnvelope<OrganizationMember[]>>(
      'organizations/members',
      { params }
    );
    return {
      members: response.data.data ?? [],
      total: response.data.pagination?.total ?? response.data.data?.length ?? 0,
    };
  },

  getInvitations: async (): Promise<OrganizationInvitation[]> => {
    const response = await accountAxios.get<ApiEnvelope<OrganizationInvitation[]>>(
      'organizations/invitations'
    );
    return response.data.data ?? [];
  },

  /** `roleId` is required by the API; `message` and `expiryDate` are optional. */
  sendInvitation: async (input: {
    email: string;
    roleId: string;
    message?: string;
    expiryDate?: string;
  }): Promise<OrganizationInvitation> => {
    const response = await accountAxios.post<ApiEnvelope<OrganizationInvitation>>(
      'organizations/invitations',
      input
    );
    return response.data.data;
  },
};

/** The signed-in person's own employee profile (name, contact, timezone). */
export const accountProfileApi = {
  get: async (): Promise<Record<string, unknown>> => {
    const response = await accountAxios.get<ApiEnvelope<Record<string, unknown>>>('my-profile');
    return response.data.data;
  },

  update: async (input: {
    firstName?: string;
    lastName?: string;
    middleName?: string | null;
  }): Promise<Record<string, unknown>> => {
    const response = await accountAxios.patch<ApiEnvelope<Record<string, unknown>>>(
      'my-profile',
      input
    );
    return response.data.data;
  },
};
